<div class="page" (click)="closeInfo()">
  <h1>AFib Waitlist Dashboard</h1>

  <!-- View mode tabs -->
  <div class="view-tabs">
    <button
      type="button"
      class="view-tab"
      [class.view-tab--active]="viewMode() === 'map'"
      (click)="$event.stopPropagation(); setViewMode('map')"
    >
      Map
    </button>

    <button
      type="button"
      class="view-tab"
      [class.view-tab--active]="viewMode() === 'descriptive'"
      (click)="$event.stopPropagation(); setViewMode('descriptive')"
    >
      Descriptive statistics
    </button>

    <button
      type="button"
      class="view-tab"
      [class.view-tab--active]="viewMode() === 'schedule'"
      (click)="$event.stopPropagation(); setViewMode('schedule')"
    >
      Drill-Down
    </button>
  </div>

  <!-- Loading / error states -->
  <section *ngIf="loading()" class="status status--loading">
    Loading synthetic waitlist data…
  </section>

  <section *ngIf="error() as err" class="status status--error">
    {{ err }}
  </section>

  <!-- MAP VIEW PANEL -->
  <div class="view-panel" [class.view-panel--active]="viewMode() === 'map'">
    
    <section class="grid">
      <div class="card">
        <h2>Patient Distribution</h2>
        <div id="patient-map" class="map"></div>
        <p class="hint">
          Circle size reflects the number of patients per anonymized FSA; centroids are synthetic
          to protect privacy.
        </p>
      </div>
    </section>
  </div>

  <!-- DESCRIPTIVE STATISTICS PANEL -->
  <div class="view-panel" [class.view-panel--active]="viewMode() === 'descriptive'">
    <section class="desc-layout">
      <!-- LEFT: main descriptive content -->
      <div class="card descriptive-card">
        <h2>Descriptive statistics</h2>

        <p class="desc-intro">
          Overview of the AF ablation waitlist cohort. Use the filters and charts below
          to understand demand, flow, backlog composition, and equity.
        </p>

        <!-- LEFT 3 KPI tiles (click i) -->
        <div class="summary-grid" *ngIf="descriptiveSummary() as d">

          <div class="summary-item kpi-tile">
            <button class="info-btn"
              (click)="$event.stopPropagation(); toggleInfo('avgWait1')">i</button>
            <div class="info-popover"
              *ngIf="openInfoKey() === 'avgWait1'"
              (click)="$event.stopPropagation()">
              {{ infoText['avgWait1'] }}
            </div>

            <div class="summary-label">Average Wait Time 1</div>
            <div class="summary-value">
              {{ d.avgWait1 != null ? (d.avgWait1 | number:'1.0-1') + ' d' : '—' }}
            </div>
          </div>

          <div class="summary-item kpi-tile">
            <button class="info-btn"
              (click)="$event.stopPropagation(); toggleInfo('avgWait2')">i</button>
            <div class="info-popover"
              *ngIf="openInfoKey() === 'avgWait2'"
              (click)="$event.stopPropagation()">
              {{ infoText['avgWait2'] }}
            </div>

            <div class="summary-label">Average Wait Time 2</div>
            <div class="summary-value">
              {{ d.avgWait2 != null ? (d.avgWait2 | number:'1.0-1') + ' d' : '—' }}
            </div>
          </div>

          <div class="summary-item kpi-tile">
            <button class="info-btn"
              (click)="$event.stopPropagation(); toggleInfo('avgTotalWait')">i</button>
            <div class="info-popover"
              *ngIf="openInfoKey() === 'avgTotalWait'"
              (click)="$event.stopPropagation()">
              {{ infoText['avgTotalWait'] }}
            </div>

            <div class="summary-label">Average Total Wait Time</div>
            <div class="summary-value">
              {{ d.avgTotalWait != null ? (d.avgTotalWait | number:'1.0-1') + ' d' : '—' }}
            </div>
          </div>

        </div>

        <!-- Demand vs capacity (line chart) -->
        <div class="chart-block" (click)="$event.stopPropagation()">
          <button class="info-btn"
            (click)="$event.stopPropagation(); toggleInfo('demandCapacity')">i</button>
          <div class="info-popover"
            *ngIf="openInfoKey() === 'demandCapacity'"
            (click)="$event.stopPropagation()">
            {{ infoText['demandCapacity'] }}
          </div>

          <h3 class="chart-title">Demand vs. Capacity Over Time</h3>

          <div class="desc-filters">
            <div class="filter-group">
              <label for="physFilter">Physician</label>
              <select
                id="physFilter"
                (change)="setDescPhysicianFilter($any($event.target).value)"
              >
                <option value="all">All</option>
                <option *ngFor="let p of physicianOptions()" [value]="p">
                  {{ p }}
                </option>
              </select>
            </div>

            <div class="filter-group">
              <label for="demandStart">Start date</label>
              <input
                id="demandStart"
                type="date"
                min="2025-01-01"
                max="2025-12-31"
                [value]="demandStartDate()"
                (change)="setDemandStartDate($any($event.target).value)"
              />
            </div>

            <div class="filter-group">
              <label for="demandEnd">End date</label>
              <input
                id="demandEnd"
                type="date"
                min="2025-01-01"
                max="2025-12-31"
                [value]="demandEndDate()"
                (change)="setDemandEndDate($any($event.target).value)"
              />
            </div>
          </div>

          <div class="chart-wrapper">
            <canvas #episodesChart></canvas>
          </div>
        </div>

        <!-- First chart row: wait by priority + waitlisted by physician -->
        <div class="chart-row">
          <div class="chart-subcard">
            <button class="info-btn"
              (click)="$event.stopPropagation(); toggleInfo('avgWaitPriority')">i</button>
            <div class="info-popover"
              *ngIf="openInfoKey() === 'avgWaitPriority'"
              (click)="$event.stopPropagation()">
              {{ infoText['avgWaitPriority'] }}
            </div>

            <h4>Average wait by priority</h4>
            <div class="chart-wrapper-small">
              <canvas #waitPriorityChart></canvas>
            </div>
          </div>

          <div class="chart-subcard">
            <button class="info-btn"
              (click)="$event.stopPropagation(); toggleInfo('waitlistedByPhys')">i</button>
            <div class="info-popover"
              *ngIf="openInfoKey() === 'waitlistedByPhys'"
              (click)="$event.stopPropagation()">
              {{ infoText['waitlistedByPhys'] }}
            </div>

            <h4>Waitlisted patients by physician &amp; priority</h4>
            <div class="chart-wrapper-small">
              <canvas #backlogChart></canvas>
            </div>
          </div>
        </div>

        <!-- Second chart row: age × sex + wait-time histogram -->
        <div class="chart-row">
          <div class="chart-subcard">
            <button class="info-btn"
              (click)="$event.stopPropagation(); toggleInfo('ageSex')">i</button>
            <div class="info-popover"
              *ngIf="openInfoKey() === 'ageSex'"
              (click)="$event.stopPropagation()">
              {{ infoText['ageSex'] }}
            </div>

            <h4>Age &amp; sex distribution</h4>
            <div class="chart-wrapper-small">
              <canvas #ageSexChart></canvas>
            </div>
          </div>

          <div class="chart-subcard">
            <button class="info-btn"
              (click)="$event.stopPropagation(); toggleInfo('waitDist')">i</button>
            <div class="info-popover"
              *ngIf="openInfoKey() === 'waitDist'"
              (click)="$event.stopPropagation()">
              {{ infoText['waitDist'] }}
            </div>

            <h4>Wait-time distribution</h4>
            <div class="chart-wrapper-small">
              <canvas #waitHistChart></canvas>
            </div>
          </div>
        </div>
      </div>

      <!-- RIGHT PANEL -->
      <div class="card bubble-card">

        <!-- RIGHT 3 KPI tiles (click i) -->
        <div class="summary-grid side-summary-grid" *ngIf="waitlistStatusSummary() as s">

          <div class="summary-item kpi-tile">
            <button class="info-btn"
              (click)="$event.stopPropagation(); toggleInfo('completed')">i</button>
            <div class="info-popover"
              *ngIf="openInfoKey() === 'completed'"
              (click)="$event.stopPropagation()">
              {{ infoText['completed'] }}
            </div>

            <div class="summary-label">Total Ablations Completed</div>
            <div class="summary-value">{{ s.completed }}</div>
          </div>

          <div class="summary-item kpi-tile">
            <button class="info-btn"
              (click)="$event.stopPropagation(); toggleInfo('onWaitlist')">i</button>
            <div class="info-popover"
              *ngIf="openInfoKey() === 'onWaitlist'"
              (click)="$event.stopPropagation()">
              {{ infoText['onWaitlist'] }}
            </div>

            <div class="summary-label">Total Patients on Waitlist</div>
            <div class="summary-value">{{ s.onWaitlist }}</div>
          </div>

          <div class="summary-item kpi-tile">
            <button class="info-btn"
              (click)="$event.stopPropagation(); toggleInfo('scheduled')">i</button>
            <div class="info-popover"
              *ngIf="openInfoKey() === 'scheduled'"
              (click)="$event.stopPropagation()">
              {{ infoText['scheduled'] }}
            </div>

            <div class="summary-label">Total Patients Scheduled</div>
            <div class="summary-value">{{ s.scheduled }}</div>
          </div>
        </div>

        <!-- QoL -->
        <div class="qol-wrap" (click)="$event.stopPropagation()">

          <!-- Title row (matches Demand vs Capacity pattern) -->
          <div class="chart-title-row">
            <h3 class="side-title">Quality of Life (AFEQT)</h3>

            <button
              class="info-btn"
              (click)="$event.stopPropagation(); toggleInfo('qol')"
            >
              i
            </button>

            <div
              class="info-popover"
              *ngIf="openInfoKey() === 'qol'"
              (click)="$event.stopPropagation()"
            >
              {{ infoText['qol'] }}
            </div>
          </div>

          <p class="bubble-intro">
            Average baseline vs follow-up AFEQT score (0–100) for the selected cohort.
          </p>

          <div class="qol-panel" *ngIf="qolKpi() as q">
            <div class="qol-metrics">
              <div class="qol-box">
                <div class="qol-label">Baseline</div>
                <div class="qol-value">{{ q.baseline != null ? q.baseline : '—' }}</div>
              </div>

              <div class="qol-box">
                <div class="qol-label">Follow-up</div>
                <div class="qol-value">{{ q.follow != null ? q.follow : '—' }}</div>
              </div>

              <div class="qol-box qol-box--delta">
                <div class="qol-label">Δ Change</div>
                <div class="qol-value">
                  {{ q.delta != null ? (q.delta >= 0 ? '+' : '') + q.delta : '—' }}
                </div>
              </div>
            </div>
      <div class="hint-small">Higher is better. Δ is follow-up minus baseline.</div>
            <div class="qol-dist" *ngIf="qolDist() as qd">
              <div class="qol-dist-row">
                <div class="qol-dist-label">≤0</div>
                <div class="qol-dist-bar">
                  <div class="qol-dist-fill" [style.width.%]="(qd.negOrZero/qd.max)*100"></div>
                </div>
                <div class="qol-dist-val">{{ qd.negOrZero }}</div>
              </div>

              <div class="qol-dist-row">
                <div class="qol-dist-label">1–10</div>
                <div class="qol-dist-bar">
                  <div class="qol-dist-fill" [style.width.%]="(qd.oneToTen/qd.max)*100"></div>
                </div>
                <div class="qol-dist-val">{{ qd.oneToTen }}</div>
              </div>

              <div class="qol-dist-row">
                <div class="qol-dist-label">11–20</div>
                <div class="qol-dist-bar">
                  <div class="qol-dist-fill" [style.width.%]="(qd.elevenToTwenty/qd.max)*100"></div>
                </div>
                <div class="qol-dist-val">{{ qd.elevenToTwenty }}</div>
              </div>

              <div class="qol-dist-row">
                <div class="qol-dist-label">&gt;20</div>
                <div class="qol-dist-bar">
                  <div class="qol-dist-fill" [style.width.%]="(qd.overTwenty/qd.max)*100"></div>
                </div>
                <div class="qol-dist-val">{{ qd.overTwenty }}</div>
              </div>

              <div class="hint-small">Distribution of QoL change (follow-up − baseline).</div>
            </div>
          </div>
        </div>

        <!-- KPI tiles -->
        <div class="chart-row kpi-placeholder-grid" (click)="$event.stopPropagation()">

          <!-- Projected backlog -->
          <div class="chart-subcard">
            <button class="info-btn"
              (click)="$event.stopPropagation(); toggleInfo('projectedBacklog')">i</button>
            <div class="info-popover"
              *ngIf="openInfoKey() === 'projectedBacklog'"
              (click)="$event.stopPropagation()">
              {{ infoText['projectedBacklog'] }}
            </div>

            <h4>Projected backlog (steady-state)</h4>

            <div class="backlog-kpi" *ngIf="backlogKpi() as k; else backlogEmpty">
              <div class="backlog-kpi-main">
                <div class="backlog-kpi-arrow" [ngClass]="k.direction">
                  <span *ngIf="k.direction === 'up'">▲</span>
                  <span *ngIf="k.direction === 'down'">▼</span>
                  <span *ngIf="k.direction === 'flat'">■</span>
                </div>

                <div class="backlog-kpi-text">
                  <div class="backlog-kpi-label">Net change / month</div>
                  <div class="backlog-kpi-value">
                    {{ k.netPerMonth >= 0 ? '+' : ''}}{{ k.netPerMonth | number:'1.0-1' }}
                  </div>
                  <div class="backlog-kpi-sub">
                    Current backlog: {{ k.current }}
                  </div>
                </div>
              </div>

              <div class="backlog-kpi-horizon">
                <div class="backlog-kpi-horizon-item">
                  <div class="h-label">3 months</div>
                  <div class="h-value">{{ k.after3 }}</div>
                </div>
                <div class="backlog-kpi-horizon-item">
                  <div class="h-label">6 months</div>
                  <div class="h-value">{{ k.after6 }}</div>
                </div>
                <div class="backlog-kpi-horizon-item">
                  <div class="h-label">12 months</div>
                  <div class="h-value">{{ k.after12 }}</div>
                </div>
              </div>
            </div>

            <ng-template #backlogEmpty>
              <div class="chart-wrapper-small kpi-placeholder">
                <span class="kpi-placeholder-label">No backlog data</span>
              </div>
            </ng-template>
          </div>

          <!-- LOS -->
          <div class="chart-subcard">
            <button class="info-btn"
              (click)="$event.stopPropagation(); toggleInfo('los')">i</button>
            <div class="info-popover"
              *ngIf="openInfoKey() === 'los'"
              (click)="$event.stopPropagation()">
              {{ infoText['los'] }}
            </div>

            <h4>Length of Stay for Admitted Patients</h4>

            <div class="wow-card" *ngIf="losKpi() as l; else losEmpty">
              <div class="wow-top">
                <div class="wow-big">{{ l.totalLos }}</div>
                <div class="wow-sub">
                  Total bed-days
                  <span *ngIf="l.avgLos != null"> • Avg LOS: {{ l.avgLos }} d</span>
                </div>
              </div>

              <div class="los-gauge" *ngIf="losGauge() as g">
                <div class="los-track">
                  <div class="los-fill" [style.width.%]="g.pct"></div>
                  <div class="los-target" [style.left.%]="g.targetPct"></div>
                </div>
                <div class="los-caption">Gauge: Avg LOS (0–10 days) • Target: 5 days</div>
              </div>
            </div>

            <ng-template #losEmpty>
              <div class="chart-wrapper-small kpi-placeholder">
                <span class="kpi-placeholder-label">No LOS data</span>
              </div>
            </ng-template>
          </div>

          <!-- ED -->
          <div class="chart-subcard">
            <button class="info-btn"
              (click)="$event.stopPropagation(); toggleInfo('ed')">i</button>
            <div class="info-popover"
              *ngIf="openInfoKey() === 'ed'"
              (click)="$event.stopPropagation()">
              {{ infoText['ed'] }}
            </div>

            <h4># of ED Visits due to AFib</h4>

            <div class="wow-card" *ngIf="edSpark() as e; else edEmpty">
              <div class="wow-top">
                <div class="wow-big">{{ e.total }}</div>
                <div class="wow-sub">Total ED encounters (AFib)</div>
              </div>

              <div class="spark-wrap">
                <canvas #edSparkChart></canvas>
              </div>
            </div>

            <ng-template #edEmpty>
              <div class="chart-wrapper-small kpi-placeholder">
                <span class="kpi-placeholder-label">No ED data</span>
              </div>
            </ng-template>
          </div>

          <!-- Acute -->
          <div class="chart-subcard">
            <button class="info-btn"
              (click)="$event.stopPropagation(); toggleInfo('acute')">i</button>
            <div class="info-popover"
              *ngIf="openInfoKey() === 'acute'"
              (click)="$event.stopPropagation()">
              {{ infoText['acute'] }}
            </div>

            <h4># of Acute Care Cases</h4>

            <div class="wow-card" *ngIf="acuteSpark() as a; else acuteEmpty">
              <div class="wow-top">
                <div class="wow-big">{{ a.total }}</div>
                <div class="wow-sub">
                  {{ a.patientsAdmitted }} patients admitted • {{ a.rate }}%
                </div>
              </div>

              <div class="spark-wrap">
                <canvas #acuteSparkChart></canvas>
              </div>
            </div>

            <ng-template #acuteEmpty>
              <div class="chart-wrapper-small kpi-placeholder">
                <span class="kpi-placeholder-label">No acute data</span>
              </div>
            </ng-template>
          </div>

        </div>
      </div>
    </section>
  </div>

<!-- SCHEDULE (WORKLIST) VIEW PANEL -->
<div class="view-panel" [class.view-panel--active]="viewMode() === 'schedule'">
  <section class="schedule-layout">
    <div class="card has-info" (click)="$event.stopPropagation()">
      <div class="chart-title-row">
        <h2>Drill-Down Table</h2>

        <button
          class="info-btn"
          (click)="$event.stopPropagation(); toggleInfo('scheduleWorklist')"
        >
          i
        </button>

        <div
          class="info-popover"
          *ngIf="openInfoKey() === 'scheduleWorklist'"
          (click)="$event.stopPropagation()"
        >
          {{ infoText['scheduleWorklist'] }}
        </div>
      </div>

      <p class="desc-intro">
        Line-by-line list of patients who are <b>Scheduled</b> or <b>On waitlist</b>.
      </p>

      <!-- Filters (SAFE: wired to stub methods) -->
      <div class="worklist-filters">
        <div class="filter-group">
          <label>Status</label>
          <select (change)="setSchedStatus($any($event.target).value)">
            <option value="all">All</option>
            <option value="scheduled">Scheduled</option>
            <option value="on_waitlist">On waitlist</option>
          </select>
        </div>

        <div class="filter-group">
          <label>Priority</label>
          <select (change)="setSchedPriority($any($event.target).value)">
            <option value="all">All</option>
            <option value="urgent">Urgent</option>
            <option value="priority">Priority</option>
            <option value="elective">Elective</option>
          </select>
        </div>

        <div class="filter-group">
          <label>Physician</label>
          <select (change)="setSchedPhysician($any($event.target).value)">
            <option value="all">All</option>
            <option *ngFor="let p of schedulePhysicianOptions()" [value]="p">{{ p }}</option>
          </select>
        </div>

        <div class="filter-group">
          <label>Search (Patient / Referral)</label>
          <input
            type="text"
            [value]="schedSearch()"
            (input)="setSchedSearch($any($event.target).value)"
            placeholder="e.g., P0012"
          />
        </div>
        </div> <!-- END worklist-filters -->

<div class="worklist-table-wrap" *ngIf="scheduleWorklist() as wl">
  <table class="worklist-table" *ngIf="wl.length; else emptyWorklist">
<thead>
  <tr>
    <th class="sortable" (click)="toggleWorklistSort('patient')">
      Patient
      <span class="sort-indicator">{{ getSortIcon('patient') }}</span>
    </th>

    <th class="num sortable" (click)="toggleWorklistSort('age')">
      Age
      <span class="sort-indicator">{{ getSortIcon('age') }}</span>
    </th>

    <th class="sortable" (click)="toggleWorklistSort('sex')">
      Sex
      <span class="sort-indicator">{{ getSortIcon('sex') }}</span>
    </th>

    <th class="sortable" (click)="toggleWorklistSort('status')">
      Status
      <span class="sort-indicator">{{ getSortIcon('status') }}</span>
    </th>

    <th class="sortable" (click)="toggleWorklistSort('priority')">
      Priority
      <span class="sort-indicator">{{ getSortIcon('priority') }}</span>
    </th>

    <th class="sortable" (click)="toggleWorklistSort('physician')">
      Physician
      <span class="sort-indicator">{{ getSortIcon('physician') }}</span>
    </th>

    <th class="sortable" (click)="toggleWorklistSort('referral')">
      Referral
      <span class="sort-indicator">{{ getSortIcon('referral') }}</span>
    </th>

    <th class="sortable" (click)="toggleWorklistSort('scheduled')">
      Scheduled
      <span class="sort-indicator">{{ getSortIcon('scheduled') }}</span>
    </th>

    <th class="num sortable" (click)="toggleWorklistSort('days_waiting')">
      Days waiting
      <span class="sort-indicator">{{ getSortIcon('days_waiting') }}</span>
    </th>

    <th class="num sortable" (click)="toggleWorklistSort('cha2')">
      CHA₂DS₂-VASc
      <span class="sort-indicator">{{ getSortIcon('cha2') }}</span>
    </th>

    <th class="num sortable" (click)="toggleWorklistSort('comorb')">
      Comorbidities
      <span class="sort-indicator">{{ getSortIcon('comorb') }}</span>
    </th>

    <th class="num sortable" (click)="toggleWorklistSort('ed')">
      ED
      <span class="sort-indicator">{{ getSortIcon('ed') }}</span>
    </th>

    <th class="num sortable" (click)="toggleWorklistSort('acute')">
      Acute
      <span class="sort-indicator">{{ getSortIcon('acute') }}</span>
    </th>
  </tr>
</thead>

    <tbody>
  <tr *ngFor="let r of wl" [ngClass]="getRowClass(r)">
    <td class="mono">{{ r.Patient_ID }}</td>
    <td class="num">{{ getAge(r) ?? '—' }}</td>
    <td>{{ r.Sex || '—' }}</td>

    <td>
      <span class="badge" [ngClass]="'badge-' + normStatus(r.Current_Status)">
        {{ getStatusLabel(r) }}
      </span>
    </td>

    <td>
      <span class="pill" [ngClass]="'pill-' + normPriority(r.Priority_Level)">
        {{ getPriorityLabel(r) }}
      </span>
    </td>

    <td>{{ r.Assigned_Physician || '—' }}</td>
    <td class="mono">{{ getReferralDate(r) || '—' }}</td>
    <td class="mono">{{ getScheduledDate(r) || '—' }}</td>

    <td class="num">{{ getDaysWaiting(r) ?? '—' }}</td>
    <td class="num">{{ getCha2(r) ?? '—' }}</td>
    <td class="num">{{ getComorbCount(r) ?? '—' }}</td>
    <td class="num">{{ getEdVisits(r) ?? '—' }}</td>
    <td class="num">{{ getAcuteCases(r) ?? '—' }}</td>
  </tr>
</tbody>
  </table>

  <ng-template #emptyWorklist>
    <p class="hint">No patients match the current filters.</p>
  </ng-template>
</div>